/**
 * BS-CL-DP Dividend Payables schedule (fixed 3 CommonWp rows).
 * Audited / PerCheck = b/f + declared − paid (closing payable c/f).
 */

import {daysBetween} from '@/utils/dataUtils';

export const DIVIDEND_PAYABLE_CODE = 'BS-CL-DP';

/** Canonical b/f label (display / seed). Matching accepts legacy "b/f". */
export const DIVIDEND_PAYABLE_ITEM_BF = 'Dividend payable B/F';

/** Default declared/paid seeds use "period"; CommonWp rewrites to year when span ≥ 365. */
export const DIVIDEND_PAYABLE_ITEM_DECLARED = 'Dividend for the period';
export const DIVIDEND_PAYABLE_ITEM_PAID = 'Dividend paid during the period';

export type DividendPeriodWord = 'period' | 'year';

export const DIVIDEND_PAYABLE_DEFAULT_ITEMS = [
    DIVIDEND_PAYABLE_ITEM_BF,
    DIVIDEND_PAYABLE_ITEM_DECLARED,
    DIVIDEND_PAYABLE_ITEM_PAID,
] as const;

export function isDividendPayableCode(code: string | null | undefined): boolean {
    return String(code || '').trim().toUpperCase() === DIVIDEND_PAYABLE_CODE;
}

/**
 * period to − period from (inclusive days) ≥ 365 → year, else period.
 */
export function resolveDividendPeriodWord(
    periodFrom?: string | null,
    periodTo?: string | null,
): DividendPeriodWord {
    const days = daysBetween(periodFrom, periodTo);
    return days != null && days >= 365 ? 'year' : 'period';
}

export function dividendPayableDeclaredItem(word: DividendPeriodWord): string {
    return `Dividend for the ${word}`;
}

export function dividendPayablePaidItem(word: DividendPeriodWord): string {
    return `Dividend paid during the ${word}`;
}

function normItem(item: unknown): string {
    return String(item || '').trim();
}

/** Match b/f including legacy "Dividend payable b/f". */
export function isDividendPayableBfItem(item: unknown): boolean {
    return /^dividend payable b\/f$/i.test(normItem(item));
}

/** Match declared row: period | year | legacy period/year. */
export function isDividendPayableDeclaredItem(item: unknown): boolean {
    return /^dividend for the (period\/year|period|year)$/i.test(normItem(item));
}

/** Match paid row: period | year | legacy period/year. */
export function isDividendPayablePaidItem(item: unknown): boolean {
    return /^dividend paid during the (period\/year|period|year)$/i.test(normItem(item));
}

/**
 * Backend DB returned a declared perClient (incl. 0).
 * Factory seeds null → fill from Dividend totalDividendAmount.
 */
export function hasBackendDeclaredPerClient(perClient: unknown): boolean {
    return perClient != null && perClient !== '';
}

type RowLike = {
    item?: string | null;
    perClient?: number | null;
    lastYearFinal?: number | null;
    adjustments?: Array<{adjustment?: number | null} | null> | null;
    adjustment?: number | null;
};

function rowAdj(row: RowLike, backendOnly: boolean): number {
    const adjustments = row.adjustments;
    if (Array.isArray(adjustments) && adjustments.length > 0) {
        return adjustments.reduce((sum, d) => sum + (Number(d?.adjustment) || 0), 0);
    }
    if (backendOnly) return 0;
    return Number(row.adjustment) || 0;
}

/** Closing payable: b/f + declared − paid (+ adjustments). */
export function computeDividendPayableTotals(rows: RowLike[] | null | undefined) {
    let bf = 0;
    let declared = 0;
    let paid = 0;
    let bfLy = 0;
    let declaredLy = 0;
    let paidLy = 0;
    let adjustment = 0;
    let backendAdj = 0;
    (rows || []).forEach((r) => {
        if (!r) return;
        adjustment += rowAdj(r, false);
        backendAdj += rowAdj(r, true);
        const pc = Number(r.perClient) || 0;
        const ly = Number(r.lastYearFinal) || 0;
        const item = normItem(r.item);
        if (isDividendPayableBfItem(item)) {
            bf = pc;
            bfLy = ly;
        } else if (isDividendPayableDeclaredItem(item)) {
            declared = pc;
            declaredLy = ly;
        } else if (isDividendPayablePaidItem(item)) {
            paid = Math.abs(pc);
            paidLy = Math.abs(ly);
        }
    });
    const perClient = bf + declared - paid;
    const lastYearFinal = bfLy + declaredLy - paidLy;
    const final = perClient + adjustment;
    const perCheckFinal = perClient + backendAdj;
    return {perClient, adjustment, final, perCheckFinal, lastYearFinal};
}

/**
 * Empty WP factory rows (item locked; amounts editable except declared fills later).
 * WHY: declared perClient undefined = no DB value → CommonWp displays Dividend totalDividendAmount.
 */
export function createDividendPayableDefaultRows() {
    return DIVIDEND_PAYABLE_DEFAULT_ITEMS.map((item, idx) => ({
        id: idx + 1,
        item,
        itemEdit: false,
        perClient: isDividendPayableDeclaredItem(item) ? undefined : 0,
        lastYearFinal: 0,
    }));
}
